import { FetchResult } from "@apollo/client";
import HideImage from "@mui/icons-material/HideImage";
import { Box, IconButton, TextField, Typography, styled } from "@mui/material";
import Mousetrap from "mousetrap";
import { ReactElement, forwardRef, useImperativeHandle, useState } from "react";
import { FormattedMessage } from "react-intl";

import { FlexBox } from "~frontend/components/FlexBox";
import MediaAttachment from "~frontend/components/MediaAttachment";
import TooltipButton from "~frontend/components/TooltipButton";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useTextingHours } from "~frontend/util/textingHours";
import useAutofocusRef from "~frontend/util/useAutofocusRef";
import useMousetrap from "~frontend/util/useMousetrap";

import { Message } from "./types";

const SendMessageForm = styled("form")({
  display: "flex",
  flexDirection: "row",
  flex: 1,
});

const AttachmentContainer = styled(Box)({
  position: "relative",
  top: "8px",
  left: "8px",
  margin: 0,
  "&:has(+ textarea:focus)": {
    top: "7px",
    left: "7px",
  },
});

type Props = {
  onSubmit: (message: Message) => Promise<FetchResult<any> | undefined>;
  isOptedOut: boolean;
  mutationPending: boolean;
  textingHoursStart: string;
  textingHoursEnd: string;
  textingHoursTimezone: string;
};

export type SendMessageBoxHandle = {
  setMessage: (message: Message) => void;
  hasUnsavedChanges: () => boolean;
  focusMessageInput: () => void;
};

const SendMessageBox = forwardRef<SendMessageBoxHandle, Props>(
  (
    {
      onSubmit,
      isOptedOut,
      mutationPending,
      textingHoursStart,
      textingHoursEnd,
      textingHoursTimezone,
    }: Props,
    ref
  ): ReactElement => {
    const { subaccount } = useSubaccount()!;
    const [value, setValue] = useState<Message>({ text: "", attachments: [] });
    const intl = useIntl();
    const [textFieldAutofocus, textFieldRef] = useAutofocusRef();

    const { isInTextingHours } = useTextingHours({
      start: textingHoursStart,
      end: textingHoursEnd,
      timezone: textingHoursTimezone,
    });

    // We expose an imperative handle to the parent component to allow it to
    // set the message from the outside, and see whether there are unsaved
    // changes. We do this instead of passing in the message / setMessage as a
    // prop to avoid re-rendering the entire component when the message changes:
    // especially when the active tab is expensive, it's too slow to re-render
    // the whole thread on each keystroke.
    useImperativeHandle(ref, () => {
      return {
        setMessage: (message: Message) => setValue(message),
        hasUnsavedChanges: () =>
          !!value.text.trim() || value.attachments.length > 0,
        focusMessageInput: () => textFieldRef.current?.focus(),
      } satisfies SendMessageBoxHandle;
    }, [value, textFieldRef]);

    async function handleSubmit(
      evt?: React.FormEvent<HTMLFormElement> | Mousetrap.ExtendedKeyboardEvent
    ) {
      evt?.preventDefault();
      if (isOptedOut || !isInTextingHours || mutationPending) {
        return;
      }

      if (value.text.trim() || value.attachments.length) {
        const result = await onSubmit(value);
        if (result && !result.errors) {
          setValue({ text: "", attachments: [] });
        }
      }
    }

    useMousetrap(["meta+enter", "ctrl+enter"], handleSubmit);

    const disabled = isOptedOut || !isInTextingHours || mutationPending;

    const tooltipText = () => {
      if (isOptedOut) {
        return intl.formatMessage({
          defaultMessage: "Contact is opted-out",
          description: "Error message when trying to text an opted-out contact",
        });
      }

      if (!isInTextingHours) {
        return intl.formatMessage({
          defaultMessage: "Campaign is outside of texting hours",
          description:
            "Error message when trying to text a contact outside of hours",
        });
      }
    };

    if (subaccount.sendingDisabled) {
      return (
        <FlexBox
          alignItems="center"
          justifyContent="center"
          sx={{
            width: "100%",
            height: "37px",
            backgroundColor: palette.lightOrange,
          }}
        >
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="Messaging is paused on this subaccount"
              description="Alert for when messaging is disabled on the subaccount"
            />
          </Typography>
        </FlexBox>
      );
    }

    return (
      <>
        <Box
          sx={{
            fontWeight: 600,
            fontSize: "12px",
            color: palette.darkGray,
            height: "18px",
          }}
        >
          Your Message
        </Box>
        <SendMessageForm onSubmit={handleSubmit}>
          <TextField
            inputRef={textFieldAutofocus}
            multiline
            sx={{
              flex: 1,
              paddingTop: 0,
              marginBottom: 0,
              ...(value.attachments.length ? { borderTop: "none" } : {}),
            }}
            InputProps={{
              inputProps: {
                style: { width: "calc(100% - 24px)" },
              },
              sx: {
                flexDirection: "column",
                alignItems: "flex-start",
              },
              startAdornment: value.attachments.length ? (
                <AttachmentContainer sx={{ width: "calc(100% - 46px)" }}>
                  <IconButton
                    sx={{ position: "absolute", top: 0, right: "-32px" }}
                    onClick={() =>
                      setValue({ text: value.text, attachments: [] })
                    }
                    aria-label="remove media attachment"
                  >
                    <HideImage />
                  </IconButton>
                  {value.attachments.map(attachment => (
                    <MediaAttachment
                      key={attachment.url}
                      src={attachment.url}
                      contentType={attachment.mimeType}
                      sx={{ maxWidth: "calc(100% - 100px)" }}
                    />
                  ))}
                </AttachmentContainer>
              ) : undefined,
              placeholder: !isInTextingHours
                ? intl.formatMessage({
                    defaultMessage: "Campaign is outside of texting hours",
                    description:
                      "Error message when trying to text a contact outside of hours",
                  })
                : undefined,
            }}
            value={value.text}
            onChange={evt => {
              setValue({
                text: evt.target.value,
                attachments: value.attachments,
              });
            }}
            name="message"
            data-testId="thread-send-message-txt"
            disabled={disabled}
          />
          <FlexBox justifyContent="center" flexWrap="wrap" alignItems="center">
            <TooltipButton
              tooltipText={tooltipText()}
              sx={{
                backgroundColor: palette.darkGreen,
                marginLeft: "10px",
                height: "44px",
              }}
              type="submit"
              data-testId="thread-send-message-btn"
              disabled={disabled}
              variant="contained"
            >
              <FormattedMessage
                defaultMessage="Send"
                description="Button text to send a text message"
              />
            </TooltipButton>
          </FlexBox>
        </SendMessageForm>
      </>
    );
  }
);

export default SendMessageBox;
